import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles,
  Lightbulb,
  Hash,
  Video, 
  BarChart,
  ArrowRight,
  MessageSquare,
  Layers,
  Mail
} from 'lucide-react';
import { Shuffle } from 'lucide-react';
import FeatureGate from '../components/FeatureGate';

interface AITool {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  path: string;
}

const AITools = () => {
  const navigate = useNavigate();

  const tools: AITool[] = [
    {
      id: 'seo-caption-analyzer',
      title: 'SEO Caption Analyzer',
      description: 'Scores captions for searchability and reach on platforms like YouTube and TikTok',
      icon: BarChart,
      color: 'bg-blue-500',
      path: '/ai-tools/seo-caption-analyzer'
    },
    {
      id: 'reel-script-generator',
      title: 'Reel Script Generator',
      description: 'Create engaging 30-second scripts for Instagram Reels or TikTok videos',
      icon: Video,
      color: 'bg-purple-500',
      path: '/ai-tools/reel-script-generator'
    },
    {
      id: 'hashtag-generator',
      title: 'Hashtag Generator',
      description: 'Find the perfect hashtags to increase your content reach',
      icon: Hash,
      color: 'bg-green-500',
      path: '/ai-tools/hashtag-generator'
    },
    {
      id: 'post-idea-brainstormer',
      title: 'Post Idea Brainstormer',
      description: 'Generate creative post ideas with engaging hooks based on your goals',
      icon: Lightbulb,
      color: 'bg-amber-500',
      path: '/ai-tools/post-idea-brainstormer'
    },
    {
      id: 'carousel-outline-builder',
      title: 'Carousel Outline Builder',
      description: 'Create structured outlines for engaging carousel posts on Instagram and LinkedIn',
      icon: Layers,
      color: 'bg-rose-500',
      path: '/ai-tools/carousel-outline-builder'
    },
    {
      id: 'email-draft-generator',
      title: 'Email Draft Generator',
      description: 'Convert social media posts into professional email drafts',
      icon: Mail,
      color: 'bg-blue-500',
      path: '/ai-tools/email-draft-generator'
    }
    ,
    {
      id: 'ab-hook-tester',
      title: 'A/B Hook Tester',
      description: 'Compare different hooks for your captions to maximize engagement',
      icon: Shuffle,
      color: 'bg-purple-500',
      path: '/ai-tools/ab-hook-tester'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Tools</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          AI-powered tools to enhance your content creation workflow
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <FeatureGate key={tool.id} feature="ai_tools">
              <motion.div
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
                className="bg-white dark:bg-dark-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-dark-700 transition-all duration-200"
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center mb-4">
                    <div className={`p-3 ${tool.color} bg-opacity-10 dark:bg-opacity-20 rounded-lg`}>
                      <Icon className={`h-5 w-5 ${tool.color.replace('bg-', 'text-')}`} />
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900 dark:text-white">
                      {tool.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 flex-grow text-sm">
                    {tool.description}
                  </p>
                  <button
                    onClick={() => navigate(tool.path)}
                    className="mt-auto flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors group text-sm font-medium"
                  >
                    <span>Open Tool</span>
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.div>
            </FeatureGate>
          );
        })}
      </div>

      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-4 mb-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-xl font-semibold">AI-Powered Assistance</h3>
            <p className="text-indigo-100">Enhance your content creation workflow with our suite of AI tools</p>
          </div>
        </div>
        <p className="text-indigo-100 mb-4">
          Our AI tools are designed to help you create better content faster. From generating ideas to improving your existing content,
          we've got you covered at every step of your content creation journey.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Time-saving</span>
          <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Engagement-boosting</span>
          <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Creativity-enhancing</span>
        </div>
      </div>
      
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-100 dark:border-amber-800/30">
        <div className="flex items-center space-x-4 mb-4">
          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
            <Lightbulb className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-amber-900 dark:text-amber-100">Coming Soon</h3>
            <p className="text-amber-800 dark:text-amber-200">More AI tools are on the way to help with your content creation</p>
          </div>
        </div>
        <p className="text-amber-700 dark:text-amber-300">
          We're working on adding more powerful AI tools to help you create better content faster. Stay tuned for caption generators, content idea generators, and more!
        </p>
      </div>
    </motion.div>
  );
};

export default AITools;